// Slice #6 — Processing Dashboard + U1a lifecycle-control types.
//
// Mirrors the deployed kk-admin-sa-music-processing API:
//   Reads:     GET /processing-jobs, GET /processing-jobs/{jobId},
//              GET /approved-artifact-versions, GET /approved-artifact-versions/{id}
//   Lifecycle: POST /processing-jobs/{jobId}/reprocess (single),
//              POST /processing-jobs/reprocess (bulk ids[], max 25),
//              POST /processing-jobs/{jobId}/resume,
//              POST /approved-artifact-versions/{id}/revoke (Super Admin only)

/** Standard response envelope for ALL music-processing API responses. */
export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

export type SongType = "kk-original" | "commercial";

/** Lifecycle state enum (pipeline §9.3). */
export type ProcessingJobState =
  | "upload_initiated"
  | "upload_received"
  | "queued"
  | "awaiting_processing"
  | "upload_expired"
  | "validating"
  | "scanning"
  | "enriching"
  | "awaiting_review"
  | "linking"
  | "transcoding"
  | "finalizing"
  | "approved"
  | "not_approved"
  | "failed";

/** Terminal decision enum — sparse (empty until terminal). */
export type ProcessingDecision = "approved" | "not_approved" | "failed";

/** Per-checkpoint status. */
export type CheckpointStatus =
  | "pending"
  | "pass"
  | "fail"
  | "warning"
  | "skipped";

export interface CheckpointSummary {
  total: number;
  pass: number;
  fail: number;
  warning: number;
  pending: number;
  skipped: number;
}

/**
 * Canonical checkpoint key order — render the timeline in THIS order.
 * Matches actual pipeline execution: the transcode (audio-processing) runs
 * before catalog-linking, and track-finalized resolves before the
 * ApprovedArtifactVersion row exists.
 */
export const CANONICAL_CHECKPOINT_KEYS = [
  "upload-received",
  "songtype-gate",
  "processing-bucket",
  "validate-mp3",
  "malware-scan",
  "metadata-discovery",
  "audio-processing",
  "catalog-linking",
  "policy-check",
  "track-finalized",
  "approved-artifact-created",
  "ready-for-playback",
] as const;

export type CheckpointKey = (typeof CANONICAL_CHECKPOINT_KEYS)[number];

/**
 * States the pipeline moves through on its own. While a job is in one of
 * these, live surfaces (uploader recent jobs, job detail) poll for updates.
 * Terminal (approved / not_approved / failed / upload_expired) and parked
 * (awaiting_review) states don't change without operator action.
 */
export const IN_FLIGHT_JOB_STATES: ReadonlySet<ProcessingJobState> = new Set([
  "upload_initiated",
  "upload_received",
  "queued",
  "awaiting_processing",
  "validating",
  "scanning",
  "enriching",
  "linking",
  "transcoding",
  "finalizing",
]);

/**
 * Song Uploader intake-queue states (Option A). Must stay aligned with
 * admin-api `UPLOADER_INTAKE_QUEUE_STATES` / `matchesIntakeQueue`.
 * Successful handoff (awaiting_processing + checksum computed) is excluded.
 */
export const UPLOADER_INTAKE_QUEUE_STATES: ReadonlySet<ProcessingJobState> =
  new Set([
    "upload_initiated",
    "upload_received",
    "queued",
    "awaiting_processing",
    "failed",
    "upload_expired",
    "awaiting_review",
  ]);

/** Brief success flash after intake handoff before the local row is cleared. */
export const UPLOADER_COMPLETE_CLEAR_DELAY_MS = 3500;

/** True when a Processing Job belongs on the Song Uploader intake table. */
export function jobBelongsInUploaderIntakeQueue(job: {
  state?: string | null;
  intakeChecksumStatus?: string | null;
}): boolean {
  const state = job && typeof job.state === "string" ? job.state : "";
  if (!UPLOADER_INTAKE_QUEUE_STATES.has(state as ProcessingJobState)) {
    return false;
  }
  if (
    state === "awaiting_processing" &&
    job.intakeChecksumStatus === "computed"
  ) {
    return false;
  }
  return true;
}

/** Intake / handoff movers that should keep Uploader polling alive. */
export const UPLOADER_INTAKE_IN_FLIGHT_STATES: ReadonlySet<ProcessingJobState> =
  new Set([
    "upload_initiated",
    "upload_received",
    "queued",
    "awaiting_processing",
  ]);

/** Intermediate keys shown but de-emphasized in the timeline (spec §3.1). */
export const DEEMPHASIZED_CHECKPOINT_KEYS: ReadonlySet<string> = new Set([
  "songtype-gate",
  "processing-bucket",
]);

/** Human labels for the canonical checkpoint keys. */
export const CHECKPOINT_LABELS: Record<string, string> = {
  "upload-received": "Upload Received",
  "songtype-gate": "Song Type Classification",
  "processing-bucket": "Processing Intake Prepared",
  "validate-mp3": "MP3 Technical Validation",
  "malware-scan": "Malware Scan",
  "metadata-discovery": "Metadata Discovery and Identification",
  "catalog-linking": "Catalog Linking",
  "audio-processing": "Audio Processing",
  "policy-check": "Policy Checks",
  "approved-artifact-created": "KrossKast Playback MP3 Created",
  "track-finalized": "Track Record Finalized",
  "ready-for-playback": "Ready for Playback",
};

export type ChecksumStatus = "pending" | "computed" | "failed";

/** List projection row (GET /processing-jobs). */
export interface ProcessingJobRow {
  jobId: string;
  songType: SongType;
  state: ProcessingJobState;
  uploadSource: string;
  /** Sparse — omitted for system/anon jobs. */
  actorSub?: string;
  actorEmployeeId: string;
  actorMasterAccountId: string;
  originalFilename: string;
  /** Discovered/identified metadata — null until metadata identification. */
  discoveredTitle?: string | null;
  discoveredArtist?: string | null;
  reasonCode: string;
  currentCheckpoint: string;
  checkpointSummary: CheckpointSummary;
  metadataConfidence: number;
  malwareStatus: string;
  policyStatus: string;
  approvedArtifactVersionId: string;
  trackId: string;
  /** Sparse — present once linked. */
  lineageTrackId?: string;
  attempt: number;
  reprocessOfJobId: string;
  /** DRA-3 — initial | reprocess | replace-song-audio (sparse on older jobs). */
  jobPurpose?: string | null;
  /** DRA-3 — operator reason when jobPurpose is replace-song-audio. */
  replaceReason?: string | null;
  replaceOfTrackId?: string | null;
  replacedFromJobId?: string | null;
  correlationId: string;
  uploadSessionId?: string;
  requestId?: string;
  intakeChecksumStatus?: ChecksumStatus | null;
  intakeSizeBytes?: number | null;
  uploadCompletedAt?: string | null;
  uploadDurationMs?: number | null;
  presignedUrlExpiresAt?: string | null;
  uploadExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  /** Sparse — empty until terminal. */
  decision?: ProcessingDecision;
  /** Sparse — only present on failed jobs. */
  failedCheckpoint?: string;
  // ---- PJL-1 soft-archive lifecycle (orthogonal to pipeline state) ----------
  /** True while the job is archived (hidden from the Active view). */
  archived?: boolean;
  archivedAt?: string | null;
  archivedBy?: string | null;
  /** Nullable FOREVER — operators may archive without a reason (owner #8). */
  archiveReason?: string | null;
  /** Archive history is retained after restore (evidence immutability). */
  restoredAt?: string | null;
  restoredBy?: string | null;
  /** MDL — Linked Song ownership from Track projection (list/detail enrich). */
  linkedSong?: LinkedSongRef | null;
  /** Song Library ↔ Song Processing governance (list/detail enrich). */
  ownership?: JobOwnership | null;
  /** True iff this job is Track.sourceProcessingJobId (current Details). */
  isCurrentPlaybackJob?: boolean | null;
  /** Live Ready-for-Playback integrity (not historical checkpoint alone). */
  playbackIntegrity?: PlaybackIntegrity | null;
  /** Track’s current Details job id (for History banner navigation). */
  currentProcessingJobId?: string | null;
  /**
   * PJH — backend-authoritative retention lifecycle (detail GET).
   * UI must not hard-code Class C days; use this contract.
   */
  retentionLifecycle?: RetentionLifecycle | null;
}

/** PJH — Class C/D/E retention + operator-action contract from music-processing. */
export type RetentionClassification =
  | "C"
  | "D"
  | "E"
  | "governed"
  | "active"
  | "unclassified";

export type RetentionOperatorAction = "optional" | "required" | "none";

export interface RetentionLifecycle {
  classification: RetentionClassification;
  eligible: boolean;
  reason: string;
  /** Class C only — days from backend policy (KK_PJL_CLASS_C_RETENTION_DAYS). */
  retentionDays: number | null;
  automaticCleanup: boolean;
  /**
   * Earliest Class C eligibility instant (updatedAt + retentionDays).
   * Not a guaranteed deletion timestamp — the daily scheduler may run later.
   */
  automaticCleanupEligibleAt: string | null;
  operatorAction: RetentionOperatorAction;
}

/** MDL — Song Library ownership surface attached to Processing Jobs. */
export type SongStatus =
  | "active"
  | "archived"
  | "deleted"
  | "none"
  | "missing";

export interface LinkedSongRef {
  trackId: string | null;
  songStatus: SongStatus;
  title: string | null;
  songLibraryPath: string;
  songLibraryLabel: string;
}

/** Governed = living Song (Active/Archived); Standalone = orphan / never linked. */
export type JobOwnership = "governed" | "standalone";

export type PlaybackIntegrityStatus =
  | "ready_for_playback"
  | "integrity_issue"
  | "not_applicable";

export interface PlaybackIntegrityFailure {
  code: string;
  message: string;
}

export interface PlaybackIntegrity {
  status: PlaybackIntegrityStatus;
  failures: PlaybackIntegrityFailure[];
}

export interface ProcessingCheckpoint {
  key: string;
  status: CheckpointStatus;
  startedAt?: string | null;
  completedAt?: string | null;
  detail?: string | null;
  reasonCode?: string | null;
}

export interface ProviderMatch {
  provider: string;
  matched: boolean;
  confidence: number;
  title: string;
  artist: string;
  isrc: string;
}

export interface ProviderSummary {
  metadataConfidence: number;
  providerMatches: ProviderMatch[];
  source: string;
}

export interface MalwareScan {
  status: string;
  scanner: string;
  result: string;
  scannedAt: string;
  /** GuardDuty / scanner signature DB version — not returned by S3 tag today. */
  signatureVersion?: string | null;
  threatDetails?: string | null;
  quarantineAction?: string | null;
}

/** CPT-2 — structured MPEG frame-header validation from validate-mp3. */
export interface Mp3Validation {
  ok?: boolean;
  format?: string | null;
  codec?: string | null;
  mpegVersion?: string | null;
  mpegVersionId?: string | null;
  layer?: number | null;
  bitrateMode?: string | null;
  bitrateKbps?: number | null;
  sampleRateHz?: number | null;
  channels?: number | null;
  channelMode?: string | null;
  frameOffset?: number | null;
  frameLengthBytes?: number | null;
  frameValidation?: string | null;
  consecutiveFramesValidated?: boolean | null;
  corruptionDetected?: boolean | null;
  inspectedBytes?: number | null;
  inspectedPrefixBytes?: number | null;
  originalFileSizeBytes?: number | null;
  originalDurationMs?: number | null;
  durationPolicyResult?: string | null;
  durationPolicyNote?: string | null;
  decodeValidation?: string | null;
  decodeValidationNote?: string | null;
  overallResult?: string | null;
  validatedAt?: string | null;
  reason?: string | null;
  [key: string]: unknown;
}

export interface PolicyCheck {
  status: string;
  result: string;
  reasonCode: string;
  /** Operator-facing bypass/detail text when the gate did not evaluate. */
  detail?: string | null;
  evaluated?: boolean;
  evaluatedAt?: string | null;
  mode?: string | null;
  stage?: string | null;
  approvedWithoutPolicyEvaluation?: boolean;
}

/** Matches the API `audioProcessing` block (pipeline-audio-processing / CPT-3). */
export interface AudioProcessingOriginalEvidence {
  durationMs?: number | null;
  bitrateKbps?: number | null;
  sampleRateHz?: number | null;
  channels?: number | null;
  sizeBytes?: number | null;
  codecName?: string | null;
  lufs?: number | null;
  truePeakDb?: number | null;
  lra?: number | null;
  checksumSha256?: string | null;
  leadingSilenceMs?: number | null;
  trailingSilenceMs?: number | null;
  silenceEventCount?: number | null;
  silenceDetectThresholdDb?: number | null;
  [key: string]: unknown;
}

export interface AudioProcessingProcessedEvidence {
  recipeId?: string | null;
  versionNumber?: number | null;
  durationMs?: number | null;
  bitrateKbps?: number | null;
  sampleRateHz?: number | null;
  channels?: number | null;
  sizeBytes?: number | null;
  codecName?: string | null;
  lufsTarget?: number | null;
  lufsFinal?: number | null;
  gainAppliedDb?: number | null;
  truePeakDb?: number | null;
  truePeakTargetDb?: number | null;
  loudnessRangeTargetLu?: number | null;
  leadingSilenceTrimmedMs?: number | null;
  trailingSilenceTrimmedMs?: number | null;
  durationDeltaMs?: number | null;
  silenceTrimThresholdDb?: number | null;
  silenceTrimApplied?: boolean | null;
  limitingLikely?: boolean | null;
  normalizationType?: string | null;
  outputValidation?: string | null;
  checksumSha256?: string | null;
  approvedBucket?: string | null;
  approvedObjectKey?: string | null;
  processingDurationMs?: number | null;
  adoptedExisting?: boolean | null;
  [key: string]: unknown;
}

export interface AudioProcessing {
  status: string;
  /** CPT-3 nested schema version (3+). */
  schemaVersion?: number | null;
  original?: AudioProcessingOriginalEvidence | null;
  processed?: AudioProcessingProcessedEvidence | null;
  ffmpegRecipeId?: string | null;
  /** @deprecated API field is `lufsTarget` / `inputLufs` — kept for older rows. */
  lufs?: number | null;
  lufsTarget?: number | null;
  inputLufs?: number | null;
  silenceTrimThresholdDb?: number | null;
  bitrateKbps?: number | null;
  durationMs?: number | null;
  sampleRateHz?: number | null;
  /** @deprecated prefer sampleRateHz */
  sampleRate?: number | null;
  channels?: number | null;
  approvedBucket?: string | null;
  approvedObjectKey?: string | null;
  checksumApproved?: string | null;
  sourceIntakeChecksum?: string | null;
  versionNumber?: number | null;
  adoptedExisting?: boolean | null;
  transcodedAt?: string | null;
  processingDurationMs?: number | null;
  [key: string]: unknown;
}

export interface CatalogLinking {
  status?: string | null;
  canonicalTitle?: string | null;
  artistId?: string | null;
  artistName?: string | null;
  artistCreated?: boolean | null;
  recordLabelId?: string | null;
  recordLabelName?: string | null;
  recordLabelCreated?: boolean | null;
  albumId?: string | null;
  albumTitle?: string | null;
  albumCreated?: boolean | null;
  /** Confidence score used when resolve-or-create ran (CPT-2). */
  confidenceAtCreation?: number | null;
  matchMethod?: string | null;
  unresolved?: string[] | null;
  notes?: string | null;
  resolvedAt?: string | null;
  [key: string]: unknown;
}

export interface IntakeInfo {
  originalFilename?: string;
  titleHint?: string;
  artistNameHint?: string;
  isrcHint?: string;
  checksumStatus?: ChecksumStatus;
  checksumIntake?: string | null;
  sizeBytes?: number;
  contentType?: string;
  uploadedAt?: string | null;
  processingObjectKey?: string;
  retentionClass?: string | null;
  [key: string]: unknown;
}

// ---- CMI-3 Metadata Identification (reconciled selection + confidence) -------

/**
 * One reconciled field record (CMI-3 field-by-field reconciliation). The UI
 * consumes only this normalized shape — never provider-specific fields
 * (provider independence, owner-locked 2026-07-17). `source` is a free string:
 * extraction sources ("intake-hint" / "id3" / "filename-parse"), "operator",
 * or any provider name; unknown values render as-is.
 */
export interface ReconciledFieldRecord {
  value: string | number | null;
  source: string;
  /** Provider's original score — immutable, may be null (e.g. AudD Trial). */
  providerConfidence: number | null;
  /** KrossKast reconciled confidence for this field. */
  krosskastConfidence: number | null;
  conflict?: boolean;
  strongConflict?: boolean;
  [key: string]: unknown;
}

/** One identification provider attempt (AudD, ACRCloud, …). */
export interface IdentificationAttempt {
  provider?: string | null;
  outcome?: string | null;
  score?: number | null;
  error?: string | null;
  normalizedResult?: {
    title?: string | null;
    artist?: string | null;
    album?: string | null;
    recordLabelName?: string | null;
    releaseYear?: number | string | null;
    isrc?: string | null;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

/** Evidence block persisted by the Metadata Identification stage (CMI-1..3). */
export interface MetadataIdentificationBlock {
  contractVersion?: number;
  phases?: {
    extraction?: {
      status?: string | null;
      summary?: string | null;
      sources?: unknown;
      notes?: string[];
      [key: string]: unknown;
    };
    identification?: {
      status?: string | null;
      skipReason?: string | null;
      attempts?: IdentificationAttempt[];
      [key: string]: unknown;
    };
    reconciliation?: {
      /** Final selected metadata — what Catalog Linking / finalize consume. */
      selected?: {
        title?: string | null;
        artist?: string | null;
        album?: string | null;
        recordLabelName?: string | null;
        releaseYear?: number | string | null;
        isrc?: string | null;
        mediaSource?: string | null;
        [key: string]: unknown;
      } | null;
      fields?: Record<string, ReconciledFieldRecord>;
      corrections?: unknown[];
      [key: string]: unknown;
    };
    confidence?: {
      score?: number | null;
      band?: string | null;
      reasons?: string[];
      providerScore?: number | null;
      thresholds?: {
        autoProceed?: number | null;
        review?: number | null;
      };
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  decision?: {
    outcome?: string | null;
    reasonCode?: string | null;
    businessSummary?: string | null;
    [key: string]: unknown;
  };
  reporting?: {
    status?: string | null;
    reasons?: string[];
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

/** Canonical detail record (GET /processing-jobs/{jobId}). Superset of the row. */
export interface ProcessingJob extends ProcessingJobRow {
  checkpoints: ProcessingCheckpoint[];
  providerSummary: ProviderSummary | null;
  malwareScan: MalwareScan | null;
  /** CPT-2 — structured validate-mp3 evidence (sparse on pre-CPT-2 jobs). */
  mp3Validation?: Mp3Validation | null;
  policyCheck: PolicyCheck | null;
  audioProcessing: AudioProcessing | null;
  intake: IntakeInfo | null;
  /** Catalog resolve/create outcome from the linking step. */
  catalogLinking?: CatalogLinking | null;
  /** Finalize seam outcome (created | reused | …) when recorded. */
  seamOutcome?: string | null;
  /** CMI-3 — full Metadata Identification evidence (sparse pre-CMI jobs). */
  metadataIdentification?: MetadataIdentificationBlock | null;
  /** Discovered album — null until metadata identification resolves one. */
  discoveredAlbum?: string | null;
  /** Flattened from intake on detail GET (parity with list projection). */
  titleHint?: string | null;
  artistNameHint?: string | null;
  isrcHint?: string | null;
  /** Operator-declared acquisition Source (iTunes / CD / DMP / SKS-*). */
  mediaSource?: string | null;
  intakeChecksumIntake?: string | null;
  intakeProcessingObjectKey?: string | null;
  /** Sparse pre-CMI discovery block (may carry filenameParse featured artists). */
  metadataDiscovery?: {
    sources?: {
      filenameParse?: {
        featuredArtistHints?: string[];
        title?: string | null;
        artist?: string | null;
        commercialParse?: unknown;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    [key: string]: unknown;
  } | null;
}

export type ApprovedArtifactStatus = "active" | "superseded" | "revoked";

export interface ApprovedArtifactVersion {
  artifactVersionId: string;
  trackId: string;
  version: number;
  approvedObjectKey: string;
  checksumApproved: string;
  status: ApprovedArtifactStatus;
  sourceProcessingJobId: string;
  /** DRA-3 — how this version was produced (initial | reprocess | replace-song-audio). */
  sourceJobPurpose?: string | null;
  createdAt: string;
  ffmpegRecipeId?: string | null;
  /** Lifecycle transition fields (U1-api) — null while the version is active. */
  supersededAt?: string | null;
  supersededByArtifactVersionId?: string | null;
  revokedAt?: string | null;
  revokeReason?: string | null;
  /** True after superseded Approved-bucket object removed post-cutover. */
  playbackObjectRemoved?: boolean | null;
  /** removed | pending_retry | failed */
  playbackObjectRemovalStatus?: string | null;
  playbackObjectRemovedAt?: string | null;
  playbackObjectRemovedByArtifactVersionId?: string | null;
  playbackObjectRemoveError?: string | null;
  playbackObjectRemovalAttemptCount?: number | null;
  [key: string]: unknown;
}

/** Query args for GET /processing-jobs (filters are mutually-exclusive primary). */
export interface ListProcessingJobsArgs {
  state?: ProcessingJobState;
  decision?: ProcessingDecision;
  failedCheckpoint?: string;
  needsReview?: boolean;
  uploaderSub?: string;
  lineageTrackId?: string;
  fromDate?: string;
  toDate?: string;
  /**
   * Song Uploader intake queue — Active jobs still in intake / failed /
   * awaiting review. Mutually exclusive with other primary filters.
   */
  intakeQueue?: boolean;
  /** PJL-1 — true selects the Archived view; default (false/absent) is Active. */
  archived?: boolean;
  /**
   * Governance — true selects Song Processing History (superseded governed
   * jobs). Mutually exclusive with `archived`. Default Active excludes History.
   */
  history?: boolean;
  /**
   * PJL-3.1 — free-text search (min 2 chars) over Song Title, Artist,
   * Original Filename, Job ID, and Track ID. Scoped to the current view:
   * it composes with `archived`/`history` and the other filters, never replaces them.
   */
  search?: string;
  limit?: number;
  pageToken?: string;
  /** Dense page seek (1-based). Mutually exclusive with pageToken. */
  page?: number;
  pageSize?: number;
}

export interface ListProcessingJobsResponse {
  jobs: ProcessingJobRow[];
  pageToken: string | null;
  limit: number;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  totalExact?: boolean;
  dense?: boolean;
}

/** PJL-3.2 — server-side totals (?count=true): same filter/search scope as
 * the list, minus pagination. Totals must come from the server, never be
 * inferred from rows loaded in the browser (owner scale requirement). */
export type CountProcessingJobsArgs = Omit<
  ListProcessingJobsArgs,
  "limit" | "pageToken" | "page" | "pageSize"
>;

export interface CountProcessingJobsResponse {
  /** -1 is the client-side "counts unavailable" sentinel (API predates
   * ?count=true) — the UI hides count labels instead of rendering NaN. */
  count: number;
  /** False when the server hit its bounded scan cap — count is a floor
   * ("N+"), not an exact total. */
  exact: boolean;
  dense?: boolean;
  archived?: boolean;
  history?: boolean;
}

export type ProcessingBulkAction =
  | "archive"
  | "restore"
  | "delete"
  | "reprocess";

export type ProcessingBulkSelectionMode = "allMatching" | "ids";

export interface CreateProcessingBulkOpRequest {
  action: ProcessingBulkAction;
  idempotencyKey: string;
  selectionMode: ProcessingBulkSelectionMode;
  filter?: {
    view?: "active" | "history" | "archived";
    state?: ProcessingJobState;
    decision?: ProcessingDecision;
    needsReview?: boolean;
    failedCheckpoint?: string;
    uploaderSub?: string;
    lineageTrackId?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
  };
  excludedIds?: string[];
  ids?: string[];
  confirm?: string;
  reason?: string;
}

export interface ProcessingBulkOpCounters {
  examined: number;
  succeeded: number;
  skipped: number;
  conflicts: number;
  failed: number;
}

/** P0 deletion-scope summary after internal preflight (not a Dry Run UX step). */
export interface DestructiveDeleteDryRunSummary {
  operationId: string;
  stage: string;
  /** Wire compat — internal preflight flag; not a user-facing Dry Run step. */
  dryRun: true;
  preflight?: boolean;
  totals: {
    totalMatched: number;
    eligibleStandalone: number;
    governedProtected: number;
    ineligible: number;
    trackLinked: number;
    trackless: number;
    byState?: Record<string, number>;
    byLifecycle?: Record<string, number>;
  };
  estimatedChunks: number;
  /** Frozen eligible scope size (not an env ceiling). */
  approvedMax: number;
  approvedEligibleCount: number;
  sampleEligibleIds: string[];
  sampleProtectedIds: string[];
  confirmPhrase: string | null;
  tier: string;
  warning?: string;
}

export interface ProcessingBulkOpStatus {
  bulkOpId: string;
  action: ProcessingBulkAction;
  status: string;
  filter?: CreateProcessingBulkOpRequest["filter"];
  selectionMode: ProcessingBulkSelectionMode;
  excludedIds?: string[];
  totalMatched: number;
  counters?: ProcessingBulkOpCounters;
  createdAt?: string;
  updatedAt?: string;
  errorMessage?: string | null;
  recentItems?: Array<{
    jobId: string;
    status: string;
    message?: string | null;
    reasonCode?: string | null;
  }>;
  idempotentReplay?: boolean;
  dryRun?: DestructiveDeleteDryRunSummary | null;
  eligibleCount?: number;
  governedProtected?: number;
  ineligible?: number;
  requiresApply?: boolean;
  normalPathAllowed?: boolean;
  approvedEligibleCount?: number;
  elevatedApproved?: boolean;
  applied?: boolean;
  manifestRef?: string;
  finalOutcome?: string | null;
}

export type CreateProcessingBulkOpResponse = ProcessingBulkOpStatus;
export type GetProcessingBulkOpResponse = ProcessingBulkOpStatus;

export interface ApplyProcessingBulkOpRequest {
  bulkOpId: string;
  confirm: "DELETE";
  reason: string;
  environment?: string;
  approvedCount?: number;
  confirmPhrase?: string;
}

export interface ListApprovedArtifactVersionsArgs {
  trackId?: string;
  sourceProcessingJobId?: string;
  artifactVersionId?: string;
}

export interface ListApprovedArtifactVersionsResponse {
  artifactVersions: ApprovedArtifactVersion[];
  limit: number;
}

// ---- U1a lifecycle mutation contracts (P5b endpoints) -----------------------

/** Job states from which reprocess is allowed (mirrors the API gate). */
export const REPROCESSABLE_STATES: ReadonlySet<ProcessingJobState> = new Set([
  "approved",
  "failed",
]);

// ---- PJL-3 lifecycle management (archive / restore / hard delete) ------------

/**
 * States from which a job may be ARCHIVED (mirrors lib/archive-job.js):
 * terminal jobs archive in place; awaiting_review is terminalized to
 * not_approved (archived-by-operator) first. Actively running jobs cannot be
 * archived. Superset of REPROCESSABLE_STATES, so the Active-view selection
 * checkboxes key off this set.
 */
export const ARCHIVABLE_STATES: ReadonlySet<ProcessingJobState> = new Set([
  "approved",
  "not_approved",
  "failed",
  "upload_expired",
  "awaiting_review",
]);

/** Legacy single-request ID-array cap for archive/restore/delete/reprocess
 * HTTP endpoints (`POST /processing-jobs/archive` etc., max 25 ids).
 *
 * Applies only to those legacy bounded ID-array routes and the single-row UI
 * path that still calls them. Durable BulkOp (`…/bulk-operations`) must never
 * be driven through this ceiling — the worker discovers targets and batches
 * internally. Do not widen this constant as a client work-queue size.
 */
export const MAX_BULK_LIFECYCLE = 25;

/** P0 — normal bulk hard-delete cap (matches API NORMAL_DELETE_MAX). */
export const NORMAL_DELETE_MAX = 25;

/**
 * @deprecated No operator-facing elevated record ceiling. Eligible standalone
 * archived jobs may be deleted in one elevated confirmation at any size.
 * `approvedMax` on scope summaries equals the frozen eligible count.
 */
export const ELEVATED_DELETE_MAX_DEFAULT = Number.MAX_SAFE_INTEGER;

/** Per-job outcome inside archive/restore/delete responses (single or bulk). */
export interface LifecycleActionResult {
  jobId: string;
  ok: boolean;
  /** Archive/restore success fields. */
  archived?: boolean;
  alreadyArchived?: boolean;
  terminalized?: boolean;
  state?: ProcessingJobState;
  /** Delete success fields. */
  deleted?: boolean;
  removed?: {
    intakeObjects: number;
    quarantineObjects: number;
    identificationCacheCleared: boolean;
  };
  /** Rejection fields. */
  statusCode?: number;
  reasonCode?: string;
  message?: string;
  /** MDL — present when delete is blocked by a living Song. */
  linkedSong?: LinkedSongRef | null;
}

/** Bulk archive/restore/delete response envelope. */
export interface BulkLifecycleResponse {
  action: "archive" | "restore" | "delete";
  succeeded: number;
  rejected: number;
  results: LifecycleActionResult[];
  operationId?: string;
  approvedMax?: number;
}

/** POST /processing-jobs/{jobId}/reprocess success data. */
export interface ReprocessJobResponse {
  ok: true;
  jobId: string;
  sourceJobId: string;
  attempt: number;
  trackId: string | null;
  lineageTrackId: string | null;
  state: ProcessingJobState;
  correlationId: string;
}

/** POST /processing-jobs/reprocess (bulk) request — max 25 ids, no duplicates. */
export interface BulkReprocessRequest {
  ids: string[];
}

/** Per-source-job outcome inside the bulk response. */
export interface BulkReprocessResult {
  sourceJobId: string;
  ok: boolean;
  /** Present on success. */
  jobId?: string;
  attempt?: number;
  trackId?: string | null;
  lineageTrackId?: string | null;
  state?: ProcessingJobState;
  /** Present on rejection. */
  statusCode?: number;
  reasonCode?: string;
  message?: string;
}

export interface BulkReprocessResponse {
  requested: number;
  rejected: number;
  results: BulkReprocessResult[];
}

/** POST /processing-jobs/{jobId}/resume request — optional metadata corrections. */
export interface ResumeJobRequest {
  title?: string;
  artistName?: string;
  album?: string;
}

/** POST /processing-jobs/{jobId}/correct-metadata request fields.
 * null clears (album / releaseYear / isrc only — never blocks).
 * mediaSource is required when present and cannot be cleared. */
export interface CorrectJobMetadataFields {
  title?: string;
  artistName?: string;
  recordLabelName?: string;
  album?: string | null;
  releaseYear?: number | null;
  isrc?: string | null;
  mediaSource?: string;
}

/** MDC-1 — POST /processing-jobs/{jobId}/correct-metadata success data. */
export interface CorrectJobMetadataResponse {
  jobId: string;
  state: ProcessingJobState;
  correctedFields: string[];
  reason: string;
  correctedAt: string;
  correctedBySub: string;
  trackId: string | null;
  catalogOutcome: "corrected" | "no-op" | "no-track";
  track: Record<string, unknown> | null;
  relinks: Record<string, unknown>;
  /** Relink ambiguity reasons (e.g. "artist-ambiguous") — reported, never guessed. */
  unresolved: string[];
}

/** POST /processing-jobs/{jobId}/resume success data. */
export interface ResumeJobResponse {
  jobId: string;
  state: ProcessingJobState;
  parkPoint: "metadata-discovery" | "catalog-linking";
  parkReasonCode: string | null;
  correctedFields: string[] | null;
  executionArn: string;
}

/** POST /approved-artifact-versions/{id}/revoke success data. */
export interface RevokeArtifactResponse {
  outcome: "revoked" | "already-revoked";
  artifactVersionId: string;
  trackId: string;
  version: number;
  priorStatus?: ApprovedArtifactStatus;
  trackSeamOutcome?: string;
  approvedObjectDeleted: boolean;
  approvedObjectKey?: string;
}

/**
 * Machine-readable rejection reason surfaced by the lifecycle endpoints in
 * `details.reasonCode` (U1-api). Key values the UI messages on:
 *   source-intake-missing   → production originals are deleted post-finalize;
 *                             reprocess requires a new upload (owner D2).
 *   job-still-processing    → source job has not reached a terminal state.
 *   state-not-reprocessable → terminal but not approved/failed.
 *   state-not-awaiting-review → resume only works from awaiting_review.
 */
export type LifecycleReasonCode = string;

/** POST /uploads request (P2 upload ingress). */
export interface CreateUploadRequest {
  songType: SongType;
  originalFilename: string;
  sizeBytes: number;
  contentType: "audio/mpeg";
  /** Required acquisition Source (iTunes / CD / DMP / SKS-S / SKS-C). */
  mediaSource: string;
  title?: string;
  artistName?: string;
  isrc?: string;
  /**
   * Client-stable key so automatic retries of the same file do not create a
   * second ProcessingJob when the first init succeeded but the response was lost.
   */
  idempotencyKey?: string;
  /**
   * DUP-ui — browser-computed SHA-256 of the file (advisory hint; the server
   * recomputes the authoritative checksum at receipt). Omitted when the
   * browser cannot hash (fail-open — upload proceeds without init-time
   * checksum detection).
   */
  checksumSha256Hint?: string;
  /** Optional Genre taxonomy assignment for the whole upload batch. */
  genreId?: string;
  /** Optional Subgenre (must belong to genreId); only sent with genreId. */
  subgenreId?: string;
}

// ---- DUP-api duplicate detection (409 details on POST /uploads) --------------

/** A prior ProcessingJob carrying the same content checksum. */
export interface DuplicateJobMatch {
  jobId: string;
  state: ProcessingJobState;
  decision: string | null;
  /** PJL — archived jobs still participate in duplicate detection until hard
   * deleted; surfaced so the dialog can say why the warning fired. Optional:
   * older API responses omit it. */
  archived?: boolean;
  trackId: string | null;
  lineageTrackId: string | null;
  title: string | null;
  artistName: string | null;
  originalFilename: string | null;
  attempt: number;
  createdAt: string | null;
}

/** An existing active canonical Track with the same title + artist. */
export interface DuplicateTrackMatch {
  trackId: string;
  title: string | null;
  primaryArtistId: string | null;
  status: string | null;
  playable: boolean;
  playbackVersionNumber: number | null;
  sourceProcessingJobId: string | null;
  isrc: string | null;
  isrcMatch: boolean;
  createdAt: string | null;
}

export interface DuplicateMatches {
  /** Terminal jobs with identical content (warn). */
  checksum: DuplicateJobMatch[];
  /** In-flight/parked jobs with identical content (block). */
  checksumInFlight: DuplicateJobMatch[];
  /** Active canonical Tracks with the same title + artist (warn). */
  tracks: DuplicateTrackMatch[];
}

/** DRA-2 — machine-readable duplicate gate reason on POST /uploads 409. */
export type DuplicateUploadReasonCode =
  | "duplicate-upload"
  | "duplicate-upload-in-flight"
  | "duplicate-upload-checksum-existing-song"
  | "duplicate-upload-checksum-orphan-job"
  | "duplicate-active-song-identity"
  | "duplicate-detection-unavailable"
  | "duplicate-override-retired";

/** DRA-2 — duplicate gate outcome (signal-specific UI). */
export type DuplicateOutcome = "BLOCK" | "STOP" | "REVIEW";

/** DRA-2 — operator actions surfaced by the duplicate gate. */
export type DuplicateAction =
  | "open-existing-song-processing"
  | "open-prior-song-processing"
  | "open-existing-song"
  | "replace-song-audio"
  | "keep-separate-after-title-change"
  | "cancel-upload";

/** DRA-3 — Replace Song Audio action from STOP gate / Song Library / Job detail. */
export interface ReplaceSongAudioAction {
  action: "replace-song-audio";
  available: boolean;
  deferredTo?: string | null;
  existingSong?: DuplicateTrackMatch | null;
  note?: string | null;
}

/** Visible warning when duplicate detection is unavailable (REVIEW). */
export interface DuplicateDetectionWarning {
  stage?: string | null;
  productionPolicy?: string | null;
  errorName?: string | null;
}

/** Parsed 409 duplicate rejection from POST /uploads. */
export interface DuplicateUploadDetails {
  reasonCode: DuplicateUploadReasonCode;
  outcome: DuplicateOutcome;
  signal: string | null;
  actions: DuplicateAction[];
  /** Retired in DRA-2 — always false for governed duplicates. */
  canOverride: boolean;
  matches: DuplicateMatches;
  existingSong: DuplicateTrackMatch | null;
  replaceSongAudio: ReplaceSongAudioAction | null;
  warning: DuplicateDetectionWarning | null;
  /** Operator-facing summary from the API when present. */
  message?: string | null;
}

/** POST /uploads response data (P2). Tags are signed into uploadUrl — not sent as headers. */
export interface CreateUploadResponse {
  jobId: string;
  uploadSessionId: string;
  correlationId: string;
  requestId: string;
  processingObjectKey: string;
  bucket: string;
  uploadUrl: string;
  presignedUrlExpiresAt: string;
  uploadExpiresAt: string;
  expiresIn: number;
  requiredHeaders: {
    "Content-Type": string;
  };
  objectTags: string;
  state: ProcessingJobState;
}

/** DRA-3 — POST /tracks/{trackId}/replace-audio request. */
export interface ReplaceSongAudioRequest {
  originalFilename: string;
  sizeBytes: number;
  contentType: "audio/mpeg";
  replaceReason: string;
  songType?: SongType;
  checksumSha256Hint?: string;
}

/** DRA-3 — POST /tracks/{trackId}/replace-audio success data (presign + linked job). */
export interface ReplaceSongAudioResponse extends CreateUploadResponse {
  trackId: string;
  jobPurpose: "replace-song-audio";
  attempt: number;
  replaceReason: string;
  replacedFromJobId: string | null;
}
